/**
 * PresenterMode — Popup window for presenter view
 * Receives slide data via BroadcastChannel from PresentationEditor
 * Shows: current slide (large), next slide preview, speaker notes, elapsed time, slide counter
 */

import { useState, useEffect, useRef } from 'react';

const SLIDE_W = 960;
const SLIDE_H = 540;
const CHANNEL_NAME = 'jubly_presenter';

// Render a single slide's shapes onto a scaled canvas
function SlidePreview({ slideData, scale = 1, label }) {
  if (!slideData) {
    return (
      <div
        className="relative overflow-hidden rounded-lg border border-white/20 flex items-center justify-center"
        style={{ width: SLIDE_W * scale, height: SLIDE_H * scale, backgroundColor: '#1e1e2e' }}
      >
        <span className="text-white/30 text-sm">{label || 'No slide'}</span>
      </div>
    );
  }

  const { bg, shapes } = slideData;

  return (
    <div
      className="relative overflow-hidden rounded-lg border border-white/20 shadow-xl"
      style={{ width: SLIDE_W * scale, height: SLIDE_H * scale, backgroundColor: bg || '#ffffff' }}
    >
      <div
        style={{
          width: SLIDE_W,
          height: SLIDE_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'relative',
        }}
      >
        {(shapes || []).map((shape) => (
          <div
            key={shape.id}
            style={{
              position: 'absolute',
              left: shape.x,
              top: shape.y,
              width: shape.width,
              height: shape.height,
              backgroundColor:
                shape.type === 'image' ? 'transparent' : shape.fill || 'transparent',
              border: shape.stroke
                ? `${shape.strokeWidth || 1}px solid ${shape.stroke}`
                : 'none',
              borderRadius: shape.type === 'ellipse' ? '50%' : shape.radius || 0,
              opacity: shape.opacity ?? 1,
              zIndex: shape.zIndex || 0,
              transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
              transformOrigin: 'center center',
              display: 'flex',
              alignItems: 'center',
              justifyContent:
                shape.textAlign === 'left'
                  ? 'flex-start'
                  : shape.textAlign === 'right'
                  ? 'flex-end'
                  : 'center',
              overflow: 'hidden',
            }}
          >
            {shape.type === 'text' && (
              <span
                style={{
                  color: shape.textColor || '#1F2937',
                  fontSize: shape.fontSize || 24,
                  fontWeight: shape.fontWeight || 'normal',
                  fontStyle: shape.fontStyle || 'normal',
                  fontFamily: shape.fontFamily || 'inherit',
                  textAlign: shape.textAlign || 'center',
                  padding: '4px 8px',
                  width: '100%',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {shape.text}
              </span>
            )}
            {shape.type === 'image' && (shape.src || shape.storageKey) && (
              <img
                src={
                  shape.storageKey
                    ? `/api/work/documents/image/${shape.storageKey}`
                    : shape.src
                }
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PresenterMode() {
  const [state, setState] = useState(null); // { currentSlide, nextSlide, slideIndex, totalSlides, notes }
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef(null);
  const channelRef = useRef(null);

  // Start elapsed timer on first slide update
  useEffect(() => {
    if (state && !startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
  }, [state]);

  // Tick elapsed time every second
  useEffect(() => {
    const timer = setInterval(() => {
      if (startTimeRef.current) {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Listen for BroadcastChannel messages from PresentationEditor
  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event) => {
      if (event.data?.type === 'slide_update') {
        setState(event.data.payload);
      }
    };

    // Signal to the main window that presenter view is ready
    channel.postMessage({ type: 'presenter_ready' });

    return () => {
      channel.close();
    };
  }, []);

  // Format elapsed time as MM:SS
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Scale factors
  const currentScale = 0.72;  // large current slide
  const nextScale = 0.28;     // small next slide preview

  if (!state) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center gap-6">
        <div className="text-white/40 text-lg font-medium">Waiting for presentation...</div>
        <div className="text-white/20 text-sm">Start presenting in the main window to sync slides here.</div>
        <div className="mt-4 flex items-center gap-2 text-white/20 text-xs">
          <div className="w-2 h-2 rounded-full bg-white/20 animate-pulse" />
          Listening on channel: {CHANNEL_NAME}
        </div>
      </div>
    );
  }

  const { currentSlide, nextSlide, slideIndex, totalSlides, notes } = state;

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col select-none overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 bg-gray-900 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
          <span className="text-white/70 text-sm font-medium">Presenter View</span>
        </div>
        <div className="flex items-center gap-6">
          {/* Slide counter */}
          <div className="text-white/60 text-sm">
            Slide{' '}
            <span className="text-white font-semibold text-base">{slideIndex + 1}</span>
            {' '}of{' '}
            <span className="text-white/80">{totalSlides}</span>
          </div>
          {/* Elapsed timer */}
          <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-1.5">
            <svg className="w-3.5 h-3.5 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-white font-mono text-sm font-semibold">{formatTime(elapsed)}</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 min-h-0 gap-0">
        {/* Left: current slide (large) */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 gap-4">
          <div className="text-white/30 text-xs font-semibold uppercase tracking-widest mb-2">Current Slide</div>
          <SlidePreview slideData={currentSlide} scale={currentScale} />
        </div>

        {/* Right panel: next slide + notes */}
        <div className="w-80 flex flex-col bg-gray-900 border-l border-white/10 flex-shrink-0">
          {/* Next slide */}
          <div className="p-4 border-b border-white/10">
            <div className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-3">Next Slide</div>
            {nextSlide ? (
              <SlidePreview slideData={nextSlide} scale={nextScale} />
            ) : (
              <div
                className="rounded-lg border border-white/10 flex items-center justify-center"
                style={{ width: SLIDE_W * nextScale, height: SLIDE_H * nextScale, backgroundColor: '#1e1e2e' }}
              >
                <span className="text-white/20 text-xs">Last slide</span>
              </div>
            )}
          </div>

          {/* Speaker notes */}
          <div className="flex-1 flex flex-col p-4 min-h-0">
            <div className="text-white/40 text-xs font-semibold uppercase tracking-widest mb-3">Speaker Notes</div>
            <div className="flex-1 overflow-y-auto">
              {notes ? (
                <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{notes}</p>
              ) : (
                <p className="text-white/20 text-sm italic">No notes for this slide.</p>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between text-white/30 text-xs mb-1.5">
              <span>Progress</span>
              <span>{Math.round(((slideIndex + 1) / totalSlides) * 100)}%</span>
            </div>
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-orange-500 rounded-full transition-all duration-300"
                style={{ width: `${((slideIndex + 1) / totalSlides) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom hint */}
      <div className="text-center py-2 text-white/20 text-xs border-t border-white/5">
        Use arrow keys or click in the main window to advance slides
      </div>
    </div>
  );
}
